import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@crisp/crisp-ui/cabbage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faMagnifyingGlass,
} from '@crisp/crisp-ui/icons/classic/regular';
import { setThemePreference } from '@crisp/ui-toolkit';
import { fetchPage, getStoredAuthToken } from '../lib/api';
import { HastRenderer } from '../lib/renderer';
import { AuthRequiredCard, useCrispAuth, useUserAccess } from '../lib/auth0';
import { SearchModal } from '../components/search/SearchModal';
import { FeedbackWidget } from '../components/layout/FeedbackWidget';
import { trackPageView } from '../lib/analytics';
import { PageOutput } from '../types';
import { cn } from '../lib/cn';
import { DEFAULT_SITE } from '../lib/sites';
import styles from './EmbedApp.module.css';

export const EmbedApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const slugParam = searchParams.get('slug') || 'index';

  const [currentSlug, setCurrentSlug] = useState<string>(slugParam);
  const [pageData, setPageData] = useState<PageOutput | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

  const { isLoading: authLoading, isAuthenticated, getAccessToken } = useCrispAuth();
  const [accessToken, setAccessToken] = useState<string | null>(() => getStoredAuthToken());
  const [authSettled, setAuthSettled] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.site = DEFAULT_SITE.id;
  }, []);

  useEffect(() => {
    setCurrentSlug(slugParam);
  }, [slugParam]);

  const pageAccess = pageData?.access || ['public'];
  const { hasAccess, setMockRoles, setMockAuthenticated } = useUserAccess(pageAccess);

  const handleInternalNavigate = (targetSlug: string) => {
    const clean = targetSlug.replace(/^\/+/, '') || 'index';
    setCurrentSlug(clean);
    navigate(`/embed?slug=${encodeURIComponent(clean)}`, { replace: true });
  };

  const embedComponents = React.useMemo(
    () => ({
      a: ({ href, children, className = '', ...props }: any) => {
        if (!href) return <span className={className} {...props}>{children}</span>;
        const isExternal =
          href.startsWith('http://') ||
          href.startsWith('https://') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:');
        if (isExternal) {
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(styles.inlineLink, styles.externalLink, className)}
              {...props}
            >
              <span>{children}</span>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="icon-slot" />
            </a>
          );
        }
        if (href.startsWith('#')) {
          return (
            <a href={href} className={cn(styles.inlineLink, className)} {...props}>
              {children}
            </a>
          );
        }
        return (
          <button
            type="button"
            onClick={() => handleInternalNavigate(href)}
            className={cn(styles.inlineButton, className)}
          >
            {children}
          </button>
        );
      },
    }),
    [navigate]
  );

  useEffect(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ source: 'crisp-docs', type: 'CRISP_DOCS_READY' }, '*');
    }

    const handleHostMessage = (event: MessageEvent) => {
      if (!event.data || event.data.source !== 'crisp-host-app') return;

      const { type, data } = event.data;

      if (type === 'CRISP_AUTH_TOKEN') {
        if (data?.token) {
          try {
            sessionStorage.setItem('crisp_embed_token', data.token);
          } catch {}
          setAccessToken(data.token);
        }
        if (setMockAuthenticated) setMockAuthenticated(true);
        if (setMockRoles && data?.roles) {
          setMockRoles(data.roles);
        }
      } else if (type === 'CRISP_SET_THEME') {
        if (data?.theme === 'dark' || data?.theme === 'light') {
          setThemePreference(data.theme);
        }
      } else if (type === 'CRISP_NAVIGATE') {
        if (data?.slug) {
          setCurrentSlug(data.slug);
        }
      }
    };

    window.addEventListener('message', handleHostMessage);
    return () => window.removeEventListener('message', handleHostMessage);
  }, [setMockAuthenticated, setMockRoles]);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      let token: string | null = null;
      if (isAuthenticated) {
        try {
          token = await getAccessToken();
        } catch {
          token = null;
        }
      }
      if (!token) {
        token = getStoredAuthToken();
      }
      if (!cancelled) {
        if (token) setAccessToken(token);
        setAuthSettled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (!authSettled) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchPage(currentSlug, DEFAULT_SITE.id, accessToken)
      .then((data) => {
        if (cancelled) return;
        setPageData(data);
        trackPageView(data.slug, data.title, { embed: true });

        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            {
              source: 'crisp-docs',
              type: 'CRISP_DOCS_NAVIGATED',
              data: { slug: data.slug, title: data.title },
            },
            '*'
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load document');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentSlug, accessToken, authSettled]);

  useEffect(() => {
    const reportHeight = () => {
      if (window.parent && window.parent !== window) {
        const height = document.body.scrollHeight;
        window.parent.postMessage(
          {
            source: 'crisp-docs',
            type: 'CRISP_DOCS_RESIZE',
            data: { height },
          },
          '*'
        );
      }
    };

    const observer = new ResizeObserver(reportHeight);
    observer.observe(document.body);
    reportHeight();

    return () => observer.disconnect();
  }, [pageData]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.brand}>
          {currentSlug !== 'index' && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Back to Docs Home"
              onClick={() => handleInternalNavigate('index')}
              iconPrimary={<FontAwesomeIcon icon={faArrowLeft} />}
            />
          )}
          <span className={styles.brandLabel}>Crisp Docs</span>
        </div>

        <div className={styles.actions}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSearchOpen(true)}
            iconPrimary={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          >
            <span className={styles.searchLabel}>Search</span>
          </Button>

          <Button
            variant="ghost"
            size="icon-sm"
            nativeButton={false}
            render={
              <a
                href={`/${currentSlug === 'index' ? '' : currentSlug}`}
                target="_blank"
                rel="noopener noreferrer"
              />
            }
            aria-label="Open in new tab"
            iconPrimary={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner />
          <p className={styles.loadingLabel}>Loading document...</p>
        </div>
      ) : error ? (
        <div className={styles.error}>
          <p className={styles.errorTitle}>Error</p>
          <p className={styles.errorBody}>{error}</p>
          <Button variant="link" onClick={() => handleInternalNavigate('index')}>
            Return to Overview
          </Button>
        </div>
      ) : pageData ? (
        <article className={styles.article}>
          <h1 className={styles.pageTitle}>{pageData.title}</h1>
          {pageData.description && <p className={styles.description}>{pageData.description}</p>}

          {!hasAccess ? (
            <AuthRequiredCard access={pageAccess} title={pageData.title} />
          ) : (
            <HastRenderer hast={pageData.hast} customComponents={embedComponents} />
          )}

          <FeedbackWidget slug={pageData.slug} />
        </article>
      ) : null}

      <SearchModal
        isOpen={isSearchOpen}
        currentPageSlug={pageData?.slug}
        currentPageTitle={pageData?.title}
        onClose={() => setIsSearchOpen(false)}
        onSelectResult={(slug) => {
          handleInternalNavigate(slug);
        }}
      />
    </div>
  );
};

export default EmbedApp;
